import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DefaultTFuncReturn } from 'i18next';
import styled from 'styled-components';
import { BodyBold } from '@components/dashboard/Typography';
import IconArrow from '@images/vms-account/vms-icons/Arrow.svg';
import CircleArrow from '@images/vms-account/vms-icons/circle_arrow_small.svg';
import PauseIcon from '@images/vms-account/vms-icons/Pause.svg';
import RedX from '@images/vms-account/vms-icons/RedX.svg';
import CheckIcon from '@images/vms-account/vms-icons/TickGreen.svg';
import Colors from './Colors';
import LinkTranslated from './LinkTranslated';

export enum SubscriptionStatus {
  False = 'false',
  Active = 'active',
  Paused = 'paused',
  Expired = 'expired',
  Canceled = 'canceled'
}

interface TagProps {
  text: string;
  backgroundColor?: string;
  textColor?: string;
}

const Tag: React.FC<TagProps> = ({ text, backgroundColor, textColor = Colors.cream }) => (
  <TagContainer $backgroundColor={backgroundColor} $textColor={textColor}>
    {text}
  </TagContainer>
);
interface TagContainerProps {
  $backgroundColor?: string;
  $textColor?: string;
}

const TagContainer = styled.div<TagContainerProps>`
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
  border-radius: 2px;
  padding: 5px 8px;
  text-transform: uppercase;

  font-family: BrownStd;
  font-size: 11px;
  font-weight: 700;
  line-height: 13px;
  letter-spacing: 1px;
  text-align: center;
`;

export enum TagTypes {
  Shipped = 'Shipped',
  Canceled = 'Canceled',
  Pending = 'Pending',
  IncreasedRisk = 'IncreasedRisk',
  Highlighted = 'Highlighted',
  DetectedRed = 'DetectedRed',
  DetectedBlack = 'DetectedBlack',
  NotDetected = 'NotDetected',
  OutOfRange = 'OutOfRange',
  InRange = 'InRange',
  Preparing = 'Preparing'
}

const renderTag = (type: TagTypes | undefined) => {
  switch (type) {
    case TagTypes.Shipped:
      return <Tag backgroundColor={Colors.sage} textColor={Colors.forestGreen} text="Shipped" />;
    case TagTypes.Canceled:
      return <Tag backgroundColor={Colors.carnationPink} text="Canceled" />;
    case TagTypes.Preparing:
      return <Tag backgroundColor={Colors.paleGold} text="Preparing" textColor={Colors.forestGreen} />;
    case TagTypes.Pending:
      return <Tag backgroundColor={Colors.paleGold} text="Upcoming" textColor={Colors.forestGreen} />;
    case TagTypes.IncreasedRisk:
      return <Tag backgroundColor={Colors.gold} text="Increased Risk" />;
    case TagTypes.Highlighted:
      return <Tag backgroundColor={Colors.lilac} text="Highlighted" />;
    case TagTypes.DetectedRed:
      return <Tag backgroundColor={Colors.red} text="Detected" />;
    case TagTypes.DetectedBlack:
      return <Tag backgroundColor={Colors.navyBlue} text="Detected" />;
    case TagTypes.NotDetected:
      return <Tag backgroundColor={Colors.sage} text="Not Detected" textColor={Colors.forestGreen} />;
    case TagTypes.OutOfRange:
      return <Tag backgroundColor={Colors.gold} text="Out Of Range" />;
    case TagTypes.InRange:
      return <Tag backgroundColor={Colors.sage} text="In Range" textColor={Colors.forestGreen} />;
    default:
      return null;
  }
};

export interface CardProps {
  id?: string;
  backgroundColor?: string | null;
  contentBackgroundColor?: string;
  border?: string;
  borderRadius?: string;
  leading?: JSX.Element;
  title?: DefaultTFuncReturn;
  subtitle?: string;
  trailing?: JSX.Element;
  content?: JSX.Element | string | null;
  onClick?: () => any;
  cardOpen?: boolean;
  subscription?: SubscriptionStatus;
  tag?: TagTypes;
  wide?: number;
  expandable?: boolean | null;
  fullWidth?: boolean;
  disabled?: boolean;
  expandedText?: boolean;
  expandedColor?: string;
  dropdownHeight?: number;
  titleClassName?: string;
  contentClass?: string;
  style?: React.CSSProperties | undefined;
  callback?: (state: string) => void;
  link?: string;
  noGap?: boolean;
  trailingCircle?: boolean;
  isCentered?: boolean;
}

const Card: React.FC<CardProps> = ({
  id = '',
  title,
  subtitle,
  leading,
  trailing,
  content,
  wide,
  onClick,
  cardOpen = false,
  backgroundColor = `${Colors.ecru}80`,
  contentBackgroundColor,
  border,
  borderRadius = '0',
  subscription,
  tag,
  expandable = false,
  fullWidth = false,
  expandedText,
  expandedColor,
  titleClassName,
  contentClass,
  disabled,
  style,
  callback,
  link,
  noGap = false,
  trailingCircle,
  isCentered = false
}) => {
  const [expanded, setExpanded] = useState(cardOpen);
  const [clickCount, setClickCount] = useState(0);
  const contentRef = useRef(null);
  const height = 'auto';

  const getLeading = () => {
    switch (subscription) {
      case SubscriptionStatus.Active:
        return <CheckIcon />;
      case SubscriptionStatus.Paused:
        return <PauseIcon />;
      case SubscriptionStatus.Expired:
      case SubscriptionStatus.Canceled:
        return <RedX />;
      default:
        return leading;
    }
  };

  const TrailingComponent = useMemo(() => {
    let finalExpandedText = '';

    if (expandedText) {
      finalExpandedText = expanded ? 'Hide' : 'Expand';
    }

    if (expandable) {
      return (
        <>
          {finalExpandedText ? <BodyBold style={{ marginRight: '-10px' }}>{finalExpandedText}</BodyBold> : null}
          <IconArrow
            style={expanded ? { transform: 'rotate(-180deg)', transition: 'transform 0.3s', marginTop: '-3px ' } : { transition: 'transform 0.3s' }}
          />
        </>
      );
    }

    if (trailingCircle) {
      return (
        <>
          {finalExpandedText ? <BodyBold style={{ marginRight: '-10px' }}>{finalExpandedText}</BodyBold> : null}
          <CircleArrow
            style={{
              maxHeight: '12px',
              maxWidth: '12px',
              transform: `rotate(${clickCount * 360}deg)`,
              transition: 'transform 0.5s ease-in-out'
            }}
          />
        </>
      );
    }

    if (!trailing) {
      return (
        <>
          {finalExpandedText}
          <IconArrow style={{ transform: 'rotate(-90deg)' }} />
        </>
      );
    }

    return trailing;
  }, [expandable, trailingCircle, clickCount]);

  const getHeadContent = () => (
    <MainRow
      className={`card-main-row${expanded ? ' expanded' : ''}`}
      onClick={() => {
        if (link) {
          return;
        }

        setClickCount((prev) => prev + 1);
        onClick?.();
        if (expandable) {
          setExpanded((prevExpanded) => !prevExpanded);
          callback?.(expanded ? 'close' : 'open');
        }
      }}
      $hasOnclick={!!onClick || !!expandable}
      $expanded={expanded}
      $expandable={!!expandable}
      $fullWidth={fullWidth}
      $backgroundColor={backgroundColor || ''}
      $expandedColor={expandedColor}
      $isCentered={isCentered}
    >
      {getLeading()}
      <TitleWrap style={(leading || subscription) && { margin: '0 10px' }} className={titleClassName}>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </TitleWrap>
      <Trailing $isCentered={isCentered}>
        {renderTag(tag)}
        {TrailingComponent}
      </Trailing>
    </MainRow>
  );

  useEffect(() => {
    if (expandable === true) {
      setExpanded(false);
    }
  }, [expandable]);

  useEffect(() => {
    setExpanded(cardOpen);
  }, [cardOpen]);

  return (
    <Container
      id={id}
      $expanded={expanded}
      $border={border}
      $borderRadius={borderRadius}
      $wide={wide}
      $fullWidth={fullWidth}
      style={style}
      $disabled={disabled}
      $noGap={noGap}
    >
      {title || leading ? <>{link ? <LinkWrap to={link}>{getHeadContent()}</LinkWrap> : getHeadContent()}</> : null}
      {content && (
        <Content
          className="card-content-wrapper"
          ref={contentRef}
          $expandable={expandable ?? false}
          $expanded={expanded}
          $backgroundColor={contentBackgroundColor}
          style={expanded || !expandable ? { height, display: 'flex' } : { height: '0px', display: 'none' }}
        >
          <div className={`card-content ${contentClass ?? ''}`}>{content}</div>
        </Content>
      )}
    </Container>
  );
};

export default Card;

interface StyleProps {
  $backgroundColor?: string;
  $borderRadius?: string;
  $border?: string;
  $expanded?: boolean;
  $expandable?: boolean | null;
  $wide?: number;
  $fullWidth?: boolean;
  $expandedColor?: string;
  $height?: number;
  $disabled?: boolean;
  $hasOnclick?: boolean;
  $noGap?: boolean;
  $isCentered?: boolean;
}

const Container = styled.div<StyleProps>`
  border: ${(props) => props.$border};
  border-radius: ${(props) => props.$borderRadius};
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$noGap ? '' : '1px')};
  width: 100%;
  opacity: ${(props) => (props.$disabled ? '0.5' : '1')};
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};
  user-select: none;
  overflow: hidden;

  ${(props) => props.$wide && `width: calc(100% + ${props.$wide * 2}px); margin: 0 -${props.$wide}px`};

  @media (max-width: 768px) {
    ${(props) => props.$wide && `width: calc(100% + 40px); margin: 0 -20px`};
  }
`;

const MainRow = styled.div<StyleProps>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$isCentered ? 'center' : 'flex-start')};
  transition: all 0.4s;
  background: ${(props) => props.$backgroundColor};
  padding: ${(props) => (props.$fullWidth ? '20px 0' : '20px 30px')};
  ${(props) => props.$hasOnclick && 'cursor: pointer;'}
  ${(props) => props.$expanded && props.$expandedColor && `background: ${props.$expandedColor};`}

  & > svg:first-child {
    min-width: 20px;
    min-height: 20px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: ${(props) => (props.$fullWidth ? '15px 0' : '15px 20px')};
  }
`;

const TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;

  &.meta-title {
    gap: 5px;

    h5 {
      font-weight: 400;
      font-size: 12px;
      line-height: 150%;
      letter-spacing: 1px;
      text-transform: uppercase;
    }

    span {
      font-weight: 700;
      font-size: 14px;
      line-height: 140%;
      opacity: 1;
    }
  }

  &.uppercase-title {
    h5 {
      font-size: 12px;
      font-style: normal;
      font-weight: 700;
      line-height: 16px; /* 133.333% */
      letter-spacing: 1px;
      text-transform: uppercase;
    }
  }
`;

const LinkWrap = styled(LinkTranslated)`
  color: inherit;
  display: block;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  & > svg:first-child {
    min-width: 20px;
    min-height: 20px;
    flex-shrink: 0;
  }
`;

const Title = styled.h5`
  font-family: 'BrownStd';
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 150%;
    padding-top: 2px;
  }
`;

const Subtitle = styled.span`
  font-family: 'BrownStd';
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  text-align: left;
  opacity: 0.5;
`;

const Trailing = styled.div<StyleProps>`
  margin-left: ${(props) => (props.$isCentered ? '0' : 'auto')};
  display: flex;
  align-items: center;
  gap: 20px;

  svg {
    max-width: 20px;
    max-height: 20px;
    flex-shrink: 0;
  }
`;

const Content = styled.div<StyleProps>`
  background: ${(props) => props.$backgroundColor || 'transparent'};
  transition: height 0.4s;
  overflow: hidden;
  width: 100%;
  flex-direction: column;
  font-family: 'BrownStd';
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  text-align: left;

  & > div {
    padding: ${(props) => (props.$backgroundColor ? '30px' : '30px 0')};

    &.pb0 {
      padding-bottom: 0 !important;
    }
    &.pl0 {
      padding-left: 0 !important;
    }
    &.pr0 {
      padding-right: 0 !important;
    }
    &.pt0 {
      padding-top: 0 !important;
    }
    &.p0 {
      padding: 0 !important;
    }
  }

  @media (max-width: 768px) {
    padding: ${(props) => (props.$backgroundColor ? '20px' : '20px 0')};
  }
`;
